import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { BACKEND_URL } from "./config";
import { buildApiCandidates, shouldFailoverBackendRequest } from "./backendBases";
import { formatTrCepDisplay, normalizeTrCepToLocal11 } from "./utils/phoneFormat";

// Runtime API base seçimi (AuthContext/App.js ile aynı mantık).
// Dev'de daima proxy (relative path) kullan — cookie page origin'inde set edilsin.
// Yoksa: login POST 127.0.0.1:8000'e gider, cookie 127.0.0.1 domain'ine set olur,
// sonraki localhost:3000/api/auth/me cookie'siz gider → 401 → her F5'te logout.
const API = (() => {
  if (typeof window === "undefined") return BACKEND_URL;
  try {
    const host = (window.location && window.location.hostname) || "";
    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") return "";
  } catch (_) { /* ignore */ }
  return BACKEND_URL;
})();
// Aşama B (migration 0061): GKAGÖLGE gizli giriş mekanizması kaldırıldı.
const LOGIN_SEARCH_MIN_LEN = 6;
const hasEligibleLoginQuery = (value) => {
  const q = (value || "").trim().replace(/\u00a0/g, " ").replace(/\s+/g, " ");
  return q.length >= LOGIN_SEARCH_MIN_LEN && q.includes(" ");
};

/** Giriş ekranı başlığı: kurumsal tek tip görünüm (örn. ÇANKAYA BELEDİYESİ) — Türkçe i/İ dahil */
function municipalityLoginBannerText(raw) {
  const t = (raw || "").trim();
  if (!t) return "";
  return t.toLocaleUpperCase("tr-TR");
}

export default function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [municipalityName, setMunicipalityName] = useState("");
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [recoverGivenName, setRecoverGivenName] = useState("");
  const [recoverFamilyName, setRecoverFamilyName] = useState("");
  const [recoverPhone, setRecoverPhone] = useState("");
  const [recoverParola, setRecoverParola] = useState("");
  const [recoverLoading, setRecoverLoading] = useState(false);
  // "Beni Hatırla" — varsayılan İŞARETSİZ. İşaretliyken çerezler kalıcı yazılır
  // (admin'in uzun refresh süresi); işaretsizken oturum çerezi olur ve tarayıcı
  // kapanınca silinir. Varsayılan işaretliyken, çıkış yapmayı unutan kullanıcının
  // oturumu bilgisayar kapanıp açıldıktan sonra bile geri geliyordu — ortak
  // bilgisayarlarda güvenlik riski. Güvenli varsayılan: opt-in.
  const [rememberMe, setRememberMe] = useState(false);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/system-settings`);
      if (response.data?.municipality_name) {
        setMunicipalityName(response.data.municipality_name);
      }
    } catch (error) {
      console.error("Sistem ayarları yüklenirken hata:", error);
      toast.error(
        "Sistem ayarları alınamadı. Backend (8000) çalışıyor mu kontrol edin; birkaç saniye sonra sayfayı yenileyin."
      );
    } finally {
      setLoadingSettings(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Aşama B: GKAGÖLGE gizli arama / şifresiz giriş, periyodik blokaj
  // yenileme ve cross-tab senkronizasyonu kaldırıldı. Tüm kullanıcılar
  // ad+soyad ve şifre ile normal akışta giriş yapar.

  const handleLogin = async () => {
    if (!password.trim()) {
      toast.error("Lütfen şifrenizi girin");
      return;
    }
    const normalizedFullName = (searchTerm || "").trim().replace(/\u00a0/g, " ").replace(/\s+/g, " ");
    if (!hasEligibleLoginQuery(normalizedFullName)) {
      toast.error("Lütfen ad soyadı tam yazın.");
      return;
    }
    setLoginLoading(true);
    try {
      const body = {
        full_name: normalizedFullName,
        password: password.trim(),
        remember_me: rememberMe,
      };
      const candidates = buildApiCandidates(API);
      let lastErr = null;
      let res = null;
      for (const base of candidates) {
        const url = base ? `${base}/api/auth/login` : "/api/auth/login";
        try {
          res = await axios.post(url, body, { timeout: 15000 });
          break;
        } catch (err) {
          lastErr = err;
          if (shouldFailoverBackendRequest(err)) continue;
          const detail = err.response?.data?.detail;
          const msg =
            typeof detail === "string"
              ? detail
              : Array.isArray(detail)
                ? detail.join(" ")
                : detail?.msg || err.message || "Giriş yapılamadı";
          toast.error(msg);
          return;
        }
      }
      if (!res) {
        const detail = lastErr?.response?.data?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.join(" ")
              : lastErr?.message || "Sunucuya bağlanılamadı. Backend çalışıyor mu?";
        toast.error(msg);
        return;
      }
      const data = res.data || {};
      const token = data.token;
      const loggedPersonnelId = data.personnel_id || "";
      const name = data.name || "";
      const role = data.role || "park_gorevlisi";
      const credentialSetupRequired = Boolean(data.credential_setup_required);
      // Yeni cookie tabanlı oturum: backend cookie'leri set etti.
      // Geriye uyumluluk icin token hala body'de var ama uygulamadan kaldiriliyor.
      onLogin(role, loggedPersonnelId, name, token, {
        credentialSetupRequired,
        accessExpiresAt: data.access_expires_at || null,
        refreshExpiresAt: data.refresh_expires_at || null,
        rememberMe: data.remember_me ?? rememberMe,
        idleSettings: data.idle_settings || null,
        loginPayload: data,
      });
      toast.success(
        credentialSetupRequired
          ? `${name} — önce yeni şifre ve kurtarma parolası belirleyin`
          : `${name} olarak giriş yapıldı`
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.join(" ") : (detail?.msg || err.message || "Giriş yapılamadı"));
      toast.error(msg);
    } finally {
      setLoginLoading(false);
    }
  };

  const openForgotDialog = () => {
    setRecoverGivenName("");
    setRecoverFamilyName("");
    setRecoverPhone("");
    setRecoverParola("");
    setForgotOpen(true);
  };

  const handleForgotPasswordSms = async (e) => {
    e.preventDefault();
    const gn = (recoverGivenName || "").trim();
    const fn = (recoverFamilyName || "").trim();
    if (!gn || !fn) {
      toast.error("Ad ve soyad zorunludur.");
      return;
    }
    const phNorm = normalizeTrCepToLocal11(recoverPhone);
    if (phNorm.length !== 11 || !phNorm.startsWith("05")) {
      toast.error("Cep numarası 05XX XXX XX XX biçiminde, 11 hane olmalıdır (05 ile başlamalı).");
      return;
    }
    const rp = (recoverParola || "").trim();
    if (!/^[A-Za-z0-9]{8}$/.test(rp)) {
      toast.error("Kurtarma parolası tam 8 karakter olmalıdır (harf veya rakam).");
      return;
    }
    const body = {
      given_name: gn,
      family_name: fn,
      phone: phNorm,
      recovery_parola: rp,
    };
    setRecoverLoading(true);
    try {
      const candidates = buildApiCandidates(API);
      let lastErr = null;
      let res = null;
      for (const base of candidates) {
        const url = base ? `${base}/api/auth/forgot-password-sms` : "/api/auth/forgot-password-sms";
        try {
          res = await axios.post(url, body, { timeout: 25000 });
          break;
        } catch (err) {
          lastErr = err;
          if (shouldFailoverBackendRequest(err)) continue;
          const detail = err.response?.data?.detail;
          const msg =
            typeof detail === "string"
              ? detail
              : Array.isArray(detail)
                ? detail.join(" ")
                : err.message || "İşlem başarısız.";
          toast.error(msg);
          return;
        }
      }
      if (!res) {
        const detail = lastErr?.response?.data?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.join(" ")
              : lastErr?.message || "Sunucuya bağlanılamadı.";
        toast.error(msg);
        return;
      }
      const data = res.data || {};
      setForgotOpen(false);
      setRecoverGivenName("");
      setRecoverFamilyName("");
      setRecoverPhone("");
      setRecoverParola("");
      toast.success(
        data.message ||
          "SMS geldiyse: bu pencere kapandı — yeni sayfa açılmaz. Ana girişte adınızı arayıp kendinizi seçin, gelen 6 haneli kodu Şifre kutusuna yazıp «Giriş Yap» deyin; ardından yeni şifre ve kurtarma parolası ekranı gelir."
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.join(" ")
            : err.message || "İşlem başarısız.";
      toast.error(msg);
    } finally {
      setRecoverLoading(false);
    }
  };

  const searchTooShort = searchTerm.trim().length > 0 && !hasEligibleLoginQuery(searchTerm);
  const canPasswordLogin = hasEligibleLoginQuery(searchTerm);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%)'
      }}
      data-testid="login-screen"
    >
      <Card className="w-full max-w-lg shadow-2xl border-0 overflow-hidden" data-testid="login-card">
        {/* Logo Section - %10 Daha Büyütülmüş */}
        <div className="bg-white p-8 flex justify-center items-center">
          <img 
            src="/logo-belvizyon.png?v=20260217" 
            alt="BELVİZYON Logo" 
            className="w-auto object-contain"
            style={{ height: '300px', filter: 'contrast(1.2) saturate(1.3)' }}
            data-testid="login-logo"
          />
        </div>
        
        {/* Municipality Name - Yeşil Çerçeve, Beyaz İç, Yeşil Yazı - %10 Küçültülmüş */}
        <div 
          className="mx-12 my-3 py-2 px-4 text-center rounded-md border-2"
          style={{
            backgroundColor: '#ffffff',
            borderColor: '#4caf50'
          }}
          data-testid="municipality-box"
        >
          <h2 
            className="text-lg font-bold tracking-wide"
            style={{ color: '#2e7d32' }}
            data-testid="municipality-name"
          >
            {loadingSettings ? "..." : (municipalityLoginBannerText(municipalityName) || "—")}
          </h2>
        </div>

        {/* Login Form */}
        <CardContent className="p-6 bg-white space-y-5">
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-semibold text-gray-700">Kullanıcı Adı</Label>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              data-testid="personnel-search-input"
              autoComplete="off"
              spellCheck={false}
            />
            <div className="space-y-2">
              {searchTooShort ? (
                <div className="text-sm text-amber-800 text-center bg-amber-50 border border-amber-200 rounded-md px-2 py-2">
                  Ad soyadı birlikte yazın (en az {LOGIN_SEARCH_MIN_LEN} karakter); isim listesi gösterilmez.
                </div>
              ) : canPasswordLogin ? (
                <div className="text-sm text-gray-500 text-center">
                  Ad soyad ve şifrenizi girip giriş yapın.
                </div>
              ) : (
                null
              )}
            </div>
          </div>

          {canPasswordLogin && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700">Şifre</Label>
              <input
                type="password"
                placeholder="Şifrenizi veya 6 haneli kodu girin"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                data-testid="password-input"
                autoComplete="current-password"
              />
              <p className="text-xs text-gray-500">
                Yöneticinin verdiği tek kullanımlık 6 haneli kod veya şifremi unuttum ile SMS ile gelen kod bu alana yazılır.
              </p>
              <label className="flex items-center gap-2 mt-3 cursor-pointer select-none" data-testid="remember-me-row">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  data-testid="remember-me-checkbox"
                />
                <span className="text-sm text-gray-700">
                  Beni hatırla
                  <span className="ml-1 text-xs text-gray-500">
                    (bu cihazda açık kal; işaretlemezseniz tarayıcıyı kapatınca
                    yeniden giriş istenir — ortak bilgisayarda işaretlemeyin)
                  </span>
                </span>
              </label>
            </div>
          )}

          <Button 
            onClick={handleLogin} 
            className="w-full h-12 text-lg font-semibold transition-all"
            style={{
              background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 50%, #43a047 100%)'
            }}
            disabled={!canPasswordLogin || !password.trim() || loginLoading}
            data-testid="login-button"
          >
            {loginLoading ? "Giriş yapılıyor..." : "Giriş Yap"}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={openForgotDialog}
              className="text-sm font-medium text-green-700 hover:text-green-900 hover:underline"
              data-testid="forgot-password-link"
            >
              Şifremi unuttum
            </button>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-gray-400 pt-2">
            Park ve Yeşil Alan Yönetim Sistemi
          </p>
        </CardContent>
      </Card>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Şifremi unuttum</DialogTitle>
            <DialogDescription className="text-left space-y-2">
              <p>
                Kayıtlı <strong>ad</strong>, <strong>soyad</strong>, <strong>05XX XXX XX XX</strong> cep telefonu ve
                <strong> kurtarma parolası</strong> (8 karakter, büyük/küçük harf <strong>duyarlı</strong>) eşleşince SMS
                ile 6 haneli kod gönderilir.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Yeni sayfa açılmaz.</strong> «SMS ile kod belirle»ye bastıktan sonra bu pencere kapanır; aynı
                giriş ekranında ad soyadınızı yazın, <strong>Şifre</strong> kutusuna SMS’teki kodu yazıp
                «Giriş Yap» deyin — ardından ilk girişteki gibi yeni şifre ve kurtarma parolası belirlersiniz.
              </p>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPasswordSms} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="recover-given">Ad</Label>
                <p className="text-[11px] text-muted-foreground">Büyük/küçük harf fark etmez</p>
                <Input
                  id="recover-given"
                  autoComplete="given-name"
                  value={recoverGivenName}
                  onChange={(e) => setRecoverGivenName(e.target.value)}
                  disabled={recoverLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recover-family">Soyad</Label>
                <p className="text-[11px] text-muted-foreground">Büyük/küçük harf fark etmez</p>
                <Input
                  id="recover-family"
                  autoComplete="family-name"
                  value={recoverFamilyName}
                  onChange={(e) => setRecoverFamilyName(e.target.value)}
                  disabled={recoverLoading}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="recover-phone">Cep telefonu</Label>
              <Input
                id="recover-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="0532 123 45 67"
                value={formatTrCepDisplay(recoverPhone)}
                onChange={(e) => setRecoverPhone(normalizeTrCepToLocal11(e.target.value))}
                disabled={recoverLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recover-parola">Kurtarma parolası (8 karakter)</Label>
              <p className="text-[11px] text-muted-foreground">
                Büyük ve küçük harf ayrıdır (ör. A ≠ a)
              </p>
              <Input
                id="recover-parola"
                maxLength={8}
                autoComplete="off"
                value={recoverParola}
                onChange={(e) =>
                  setRecoverParola(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 8))
                }
                placeholder="Örn: ab12CD34"
                disabled={recoverLoading}
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={() => setForgotOpen(false)}
                disabled={recoverLoading}
              >
                İptal
              </Button>
              <Button type="submit" disabled={recoverLoading}>
                {recoverLoading ? "Gönderiliyor..." : "SMS ile kod belirle"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
